interface ChunkRecoveryOptions {
  maxRetries?: number;
  routeKey?: string;
  retryKey?: string;
  navigationDelay?: number;
  /** Test hook to observe recovery navigation without mocking window.location. */
  onNavigate?: (url: string) => void;
}

export function resolveSameOriginPath(route: string, origin: string): string {
  if (!route.startsWith('/') || route.startsWith('//')) {
    return '/';
  }

  try {
    const url = new URL(route, origin);
    if (url.origin !== origin) {
      return '/';
    }

    return `${url.pathname}${url.search}${url.hash}` || '/';
  } catch {
    return '/';
  }
}

const DYNAMIC_IMPORT_ERROR_PATTERNS = [
  'Failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'Failed to load module script',
  'Importing a module script failed',
  // Safari: React.lazy resolved payload with undefined module after a stale chunk
  '_result.default',
  // Chrome: same React.lazy read, without `_result` in the message
  "Cannot read properties of undefined (reading 'default')",
];

function isDynamicImportErrorMessage(message: string): boolean {
  return DYNAMIC_IMPORT_ERROR_PATTERNS.some((pattern) => message.includes(pattern));
}

/**
 * Initialize chunk recovery for Vite preload errors and module load failures.
 * Reloads the last known route after a deploy leaves the tab on a stale hashed bundle.
 */
export function initializeChunkRecovery({
  maxRetries = 3,
  routeKey = '__last_good_route',
  retryKey = 'preload_reload',
  navigationDelay = 300,
  onNavigate,
}: ChunkRecoveryOptions = {}): () => void {
  const { pushState, replaceState } = window.history;
  const abortController = new AbortController();
  const { signal } = abortController;
  let lastPath = '';

  function navigate(route: string) {
    const path = resolveSameOriginPath(route, window.location.origin);
    const url = new URL(path, window.location.origin).toString();

    if (onNavigate) {
      onNavigate(url);
      return;
    }

    window.location.replace(url);
  }

  function persistLastRoute() {
    const path = window.location.pathname + window.location.search + window.location.hash;

    if (!path.startsWith('/assets/') && path !== lastPath) {
      lastPath = path;
      sessionStorage.setItem(routeKey, path);
    }
  }

  function getSafeRoute() {
    const lastRoute = sessionStorage.getItem(routeKey);
    if (!lastRoute) return '/';

    return resolveSameOriginPath(lastRoute, window.location.origin);
  }

  function showReloadPrompt() {
    // eslint-disable-next-line no-alert -- Recovery runs outside React
    const shouldReload = window.confirm(
      'Failed to load the latest version of the application. Please reload the page to continue.',
    );
    if (!shouldReload) return;

    sessionStorage.removeItem(retryKey);
    navigate(getSafeRoute());
  }

  function recover() {
    if (!navigator.onLine) {
      // eslint-disable-next-line no-console -- Error recovery logging
      console.warn('Offline — skipping reload');
      return;
    }

    const attempts = Number(sessionStorage.getItem(retryKey) ?? 0);
    const safeRoute = getSafeRoute();

    if (attempts >= maxRetries) {
      showReloadPrompt();
      return;
    }

    // eslint-disable-next-line no-console -- Error recovery logging
    console.error('Chunk recovery triggered', {
      attempts,
      maxRetries,
      lastRoute: sessionStorage.getItem(routeKey),
      safeRoute,
      online: navigator.onLine,
    });
    sessionStorage.setItem(retryKey, String(attempts + 1));
    setTimeout(() => navigate(safeRoute), navigationDelay);
  }

  window.addEventListener('popstate', persistLastRoute, { signal });

  window.history.pushState = function interceptedPushState(
    state: unknown,
    unused: string,
    url?: string | URL | null,
  ) {
    pushState.call(this, state, unused, url);
    persistLastRoute();
  };

  window.history.replaceState = function interceptedReplaceState(
    state: unknown,
    unused: string,
    url?: string | URL | null,
  ) {
    replaceState.call(this, state, unused, url);
    persistLastRoute();
  };

  persistLastRoute();

  window.addEventListener('vite:preloadError', (event) => {
    event.preventDefault();
    recover();
  }, { signal });

  window.addEventListener('unhandledrejection', (event) => {
    const error = event.reason;
    if (typeof error?.message === 'string' && isDynamicImportErrorMessage(error.message)) {
      event.preventDefault();
      recover();
    }
  }, { signal });

  // Resource load errors do not bubble; capture is required to see module script failures.
  window.addEventListener('error', (event) => {
    const isModuleScriptTarget = event.target instanceof HTMLScriptElement
      && event.target.type === 'module';
    const hasModuleErrorMessage = isDynamicImportErrorMessage(event.message ?? '')
      || event.message?.includes('MIME type')
      || event.message?.includes('text/html');

    if (!isModuleScriptTarget && !hasModuleErrorMessage) return;

    event.preventDefault();
    recover();
  }, { signal, capture: true });

  window.addEventListener('load', () => {
    sessionStorage.removeItem(retryKey);
  }, { signal });

  return function disposeChunkRecovery() {
    abortController.abort();
    window.history.pushState = pushState;
    window.history.replaceState = replaceState;
  };
}
